<script lang="ts">
  // Turn off inheritance through attributes
  export default {
    inheritAttrs: false
  };
</script>

<script setup lang="ts">
  import type { DocumentFormSettingsKey, VideoFormSettingsKey, CustomResourceFormSettingsKey } from "../../form-settings/form-experience/form-settings-keys.js";
  import { UserMarketoInputName, type MarketoVals } from "../../types/definitions/inputs/marketo-input-names-and-values.js";
  import { MarketoAllInputMeta } from "../../types/definitions/inputs/marketo-input-meta.js";
  import type { FormWidgetData } from "../../../widgetDataTs.js";
  import { useValidations } from "../../composables/use-input-validation.js";
  import {
    useFormServices,
    useInputsMetaData,
    useInputState,
    useFormSubmit,
    type InputStateOption,
    useEclipseHtmlFormElement,
    useTranslation
  } from "../../composables/use-form-services.js";
  import { useFormUserInputValues } from "../../composables/use-form-user-input-values.js";

  import eclipseInput from "../eclipse-input.vue";
  import emailOptInCheckbox from "../email-opt-in-checkbox.vue";
  import submitButton from '../form-experience/submit-button.vue';
  import { TranslationNamespaceEnum } from "../../locale/settings/translation-namespaces.js";
  import type { SubmitButtonTranslationKey } from "../../locale/translation-keys/submit-button-text-keys";
  import { computed, ref, watch } from "vue";
  import { isNil, type Nil } from "@mcwd/typescript-type-guards";
  import { useFormLogger } from "../../composables/use-form-logger.js";
  import { GetComputedProperties, type CustomData } from "../../composables/use-custom-data-values.js";


  const props = defineProps<{
    formSettingsKey: DocumentFormSettingsKey | VideoFormSettingsKey | CustomResourceFormSettingsKey;
    widgetData: FormWidgetData<"ResourceForm" | "GatedVideoForm" | "VideoForm" | "PostEventForm" | "ContactForm">;
    customData?: CustomData | undefined;
  }>();

  const { logger } = useFormLogger();

  const { ctaButtonText } = GetComputedProperties(props.customData);

  const { FormSettings, FormClasses, FormState } = useFormServices<"MarketoForm", "resource" | "custom-resource">({ callerLabel: "resource-form-partial-gate.vue" });
  
  const{ validateGenericBusinessEmail} = useValidations();

  if (!("PartialGate" in FormSettings.enabledFields)) {
    throw new Error(`Partial Gate does not exist for formExperience '${FormSettings.formSettingsKey}'`);
  }
  const enabledFields = FormSettings.enabledFields.PartialGate satisfies readonly string[];
  type EnabledField = typeof enabledFields[number];

  const { FormInputsMeta } = useInputsMetaData({ callerLabel: "resource-form-partial-gate.vue" }) as { FormInputsMeta: Pick<MarketoAllInputMeta, EnabledField> };

  function getEnabledUserFields() {
    return enabledFields.filter(f => (UserMarketoInputName as readonly string[]).includes(f)) as Extract<EnabledField, UserMarketoInputName>[];
  }

  // Currently only handles marketo field names
  const anyEnabledUserField = getEnabledUserFields();
  type AnyEnabledUserField = typeof anyEnabledUserField[number];


  type UserMarketoValues = { [Key in AnyEnabledUserField]: Required<MarketoVals>[Key] };

  const { userInputValues, setFormUserInputValuesFromSettings } = useFormUserInputValues<AnyEnabledUserField, UserMarketoValues>({ callerLabel: "resource form partial gate" });
  setFormUserInputValuesFromSettings();

  const { setInputStates, inputStates } = useInputState<AnyEnabledUserField>({ callerLabel: "resource-form-partial-gate.vue" });
  setInputStates(userInputValues, {
    "FirstName": { required: true },
    "LastName": { required: true },
    "Email": {
      required: true,
      validator: validateGenericBusinessEmail
    },
    "Company": { required: true },
    "Email_Opt_In__c": { required: false },
  } satisfies { [Key in AnyEnabledUserField]: InputStateOption });

  const { submitForm } = useFormSubmit({ callerLabel: "resource-form-partial-gate.vue" });
  const buttonTextMapKey: SubmitButtonTranslationKey<typeof FormSettings.formSettingsKey> = `${FormSettings.formSettingsKey}-Submit-Button`; 
  const { translate } = useTranslation();

  const { setEclipseHtmlFormElement } = useEclipseHtmlFormElement({ callerLabel: "contact-form-base.vue" });
  const formElRef = ref<HTMLFormElement | Nil>(null);

  watch(() => formElRef.value, (formEl) => {
    if (!isNil(formEl)) {
      logger.debug({ formEl });
      if (!isNil(formEl)) {
        setEclipseHtmlFormElement(formEl);
      }
    }
  });
</script>

<template>
  <template v-if="FormState === 'RegularForm'">
    <slot name="heading"></slot>
    
    <form ref="formElRef" class="eclipse-form single-row" :class="FormClasses.marketoFormClass" data-testid="form-root">
      <eclipseInput v-model="userInputValues.FirstName"
        :input-state="inputStates.FirstName"
        :form-input-meta="FormInputsMeta.FirstName"
      />

      <eclipseInput v-model="userInputValues.LastName"
        :input-state="inputStates.LastName"
        :form-input-meta="FormInputsMeta.LastName"
      />

      <eclipseInput v-model="userInputValues.Email"
        :input-state="inputStates.Email"
        :form-input-meta="FormInputsMeta.Email"
      />

      <eclipseInput v-model="userInputValues.Company"
        :input-state="inputStates.Company"
        :form-input-meta="FormInputsMeta.Company"
      />

      <emailOptInCheckbox v-model="userInputValues.Email_Opt_In__c"
        :form-input-meta="FormInputsMeta.Email_Opt_In__c"
        :input-state="inputStates.Email_Opt_In__c"
        :marketo-input-name="FormInputsMeta.Email_Opt_In__c.marketoName"
      />

      <!-- Eclipse Submit Button -->
      <submitButton @submit-form="submitForm()">
        <template v-if="ctaButtonText">
          {{ ctaButtonText }}
        </template>
        <template v-else>
          {{ translate(buttonTextMapKey, { ns: TranslationNamespaceEnum.submitButtonText }) }}
        </template>
      </submitButton>
    </form>
  </template>
</template>